import api from './api';
import { CAB_DEFINITION_DEFAULTS } from '../constants/cabDefinitionOptions';
import type {
  CabDefinition,
  CabDefinitionFormData,
  CabDefinitionListParams,
  CabDefinitionListResult,
  CabDefinitionRecord,
  SaveCabDefinitionPayload,
  SaveCabDefinitionResult,
} from '../types/cabDefinition';

export function createDefaultCabDefinitionFormData(): CabDefinitionFormData {
  return {
    name: '',
    cabManager: null,
    delegates: null,
    boardMembers: [],
    boardGroups: [],
    cabType: CAB_DEFINITION_DEFAULTS.cabType,
    active: CAB_DEFINITION_DEFAULTS.active,
    conferenceType: CAB_DEFINITION_DEFAULTS.conferenceType,
    meetingSubject: '',
    teamsMeetingUrl: '',
    teamsMeetingId: '',
    teamsPasscode: '',
    conferenceDetails: '',
  };
}

export function formDataToSavePayload(data: CabDefinitionFormData): SaveCabDefinitionPayload {
  if (!data.cabManager) {
    throw new Error('CAB Manager is required');
  }

  return {
    name: data.name.trim(),
    cabManager: data.cabManager,
    delegates: data.delegates,
    boardMembers: data.boardMembers,
    boardGroups: data.boardGroups,
    cabType: data.cabType,
    active: data.active,
    conferenceType: data.conferenceType.trim(),
    meetingSubject: data.meetingSubject.trim() || undefined,
    teamsMeetingUrl: data.teamsMeetingUrl.trim(),
    teamsMeetingId: data.teamsMeetingId.trim() || undefined,
    teamsPasscode: data.teamsPasscode.trim() || undefined,
    conferenceDetails: data.conferenceDetails || undefined,
  };
}

export function formDataToUpdatePayload(
  data: CabDefinitionFormData
): Omit<SaveCabDefinitionPayload, 'name'> {
  const { name: _name, ...rest } = formDataToSavePayload(data);
  return rest;
}

export async function fetchCabDefinitions(
  params: CabDefinitionListParams
): Promise<CabDefinitionListResult> {
  const response = await api.get<CabDefinitionListResult>('/cab-definitions', {
    params: {
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      name: params.name,
      cabType: params.cabType,
      cabManager: params.cabManager,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
    },
  });
  return response.data;
}

export async function saveCabDefinition(
  payload: SaveCabDefinitionPayload
): Promise<SaveCabDefinitionResult> {
  const response = await api.post<SaveCabDefinitionResult>('/cab-definitions', payload);
  return response.data;
}

export async function updateCabDefinition(
  id: string,
  payload: Omit<SaveCabDefinitionPayload, 'name'>
): Promise<CabDefinitionRecord | null> {
  const response = await api.put<CabDefinitionRecord>(`/cab-definitions/${id}`, payload);
  return response.data;
}

export function recordToFormData(record: CabDefinitionRecord): CabDefinitionFormData {
  const nameParts = record.cabManagerName.trim().split(/\s+/);
  const firstName = nameParts[0] ?? '';
  const lastName = nameParts.slice(1).join(' ');

  return {
    name: record.name,
    cabManager: {
      id: record.cabManagerId,
      firstName,
      lastName,
      email: '',
    },
    delegates: record.delegatesGroupId
      ? { id: record.delegatesGroupId, name: record.delegatesGroupName ?? '' }
      : null,
    boardMembers: record.boardMemberIds.map((memberId, i) => ({
      id: memberId,
      firstName: record.boardMemberNames[i]?.split(/\s+/)[0] ?? '',
      lastName: record.boardMemberNames[i]?.split(/\s+/).slice(1).join(' ') ?? '',
    })),
    boardGroups: record.boardGroupIds.map((groupId, i) => ({
      id: groupId,
      name: record.boardGroupNames[i] ?? groupId,
    })),
    cabType: record.cabType,
    active: record.active,
    conferenceType: record.conferenceType || CAB_DEFINITION_DEFAULTS.conferenceType,
    meetingSubject: record.meetingSubject ?? '',
    teamsMeetingUrl: record.teamsMeetingUrl ?? '',
    teamsMeetingId: record.teamsMeetingId ?? '',
    teamsPasscode: record.teamsPasscode ?? '',
    conferenceDetails: record.conferenceDetails ?? '',
  };
}

export async function getCabDefinitionById(id: string): Promise<CabDefinitionRecord | null> {
  try {
    const response = await api.get<CabDefinitionRecord>(`/cab-definitions/${id}`);
    return response.data;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}

export const CAB_TYPE_OPTIONS = [
  { value: 'Normal', label: 'Normal' },
  { value: 'Emergency', label: 'Emergency' },
];
